/**
 * The Projects section of a group's screen: every project with how much is in
 * it and whether it is settled, the archived ones folded away underneath, and
 * New project.
 *
 * New project is a name and then, optionally, the group's recent transactions
 * to put in it — sent as one request (`POST .../projects` with
 * `transactionIds`), so the project is never briefly empty on its way to
 * holding what it was made for. A transaction already in another project says
 * so on its row, because ticking it moves it.
 *
 * A project is a label on some of the group's transactions and never a
 * ledger of its own, so nothing here shows a balance: whether it is settled is
 * the one money fact a list of projects needs, and the project's own screen
 * is where the rest of it goes.
 */

import { useState, type FormEvent } from 'react'
import type { Project, Transaction } from '../api'
import {
  describeProjectCount,
  partitionProjects,
  pickerRows,
  projectNameProblem,
  tickedIds,
  MAX_PROJECT_NAME,
} from '../lib/projects'
import { useCreateProject, useProjects } from '../queries'
import type { Route } from '../routes'
import { TransactionPicker } from './TransactionPicker'

function ProjectRow({
  project,
  groupId,
  navigate,
}: {
  project: Project
  groupId: string
  navigate: (to: Route) => void
}) {
  return (
    <li className="project">
      <button
        type="button"
        className="button--quiet project__open"
        onClick={() => navigate({ name: 'project', groupId, projectId: project.id })}
      >
        <span className="project__name">{project.name}</span>
        <span className="project__count">{describeProjectCount(project.transactionCount)}</span>
      </button>
      <span className="project__badges">
        {project.archivedAt !== null && <span className="pill">Archived</span>}
        {project.settled && project.transactionCount > 0 && (
          <span className="pill pill--confirmed">
            <span aria-hidden="true">✓</span>
            Settled
          </span>
        )}
      </span>
    </li>
  )
}

function NewProject({
  groupId,
  projects,
  transactions,
  onDone,
}: {
  groupId: string
  projects: readonly Project[]
  transactions: readonly Transaction[]
  onDone: () => void
}) {
  const [name, setName] = useState('')
  const [picking, setPicking] = useState(false)
  const [ticked, setTicked] = useState<Set<string>>(() => new Set())
  const create = useCreateProject(groupId)

  const rows = pickerRows(transactions, projects)
  const problem = projectNameProblem(name, projects)
  const chosen = picking ? tickedIds(rows, ticked) : []

  function submit(event: FormEvent) {
    event.preventDefault()
    if (problem !== null || create.isPending) return
    create.mutate({ name: name.trim(), transactionIds: chosen }, { onSuccess: onDone })
  }

  return (
    <form className="form sheet" onSubmit={submit}>
      <div className="form__row">
        <label htmlFor={`new-project-${groupId}`}>What is it called?</label>
        <input
          id={`new-project-${groupId}`}
          value={name}
          maxLength={MAX_PROJECT_NAME}
          required
          placeholder="Cabin Weekend – food"
          onChange={(event) => setName(event.target.value)}
        />
      </div>

      {picking ? (
        <TransactionPicker
          legend="Add existing transactions (optional)"
          rows={rows}
          ticked={ticked}
          onChange={setTicked}
          disabled={create.isPending}
        />
      ) : (
        <p className="actions actions--tight">
          <button type="button" className="button--quiet" onClick={() => setPicking(true)}>
            Add existing transactions…
          </button>
        </p>
      )}

      {create.isError && <p className="bad">{create.error.message}</p>}

      <p className="actions">
        <button type="submit" disabled={problem !== null || create.isPending}>
          {create.isPending
            ? 'Creating…'
            : chosen.length > 0
              ? `Create with ${chosen.length} ${chosen.length === 1 ? 'transaction' : 'transactions'}`
              : 'Create project'}
        </button>
        <button type="button" className="button--quiet" disabled={create.isPending} onClick={onDone}>
          Cancel
        </button>
        {name !== '' && problem && <span className="muted note">{problem}</span>}
      </p>
    </form>
  )
}

export function Projects({
  groupId,
  archived,
  transactions,
  navigate,
}: {
  groupId: string
  /** The *group* is archived: it takes no writes, so there is no New project. */
  archived: boolean
  /** The group's recent transactions, as its own screen already holds them. */
  transactions: readonly Transaction[]
  navigate: (to: Route) => void
}) {
  const projects = useProjects(groupId)
  const [creating, setCreating] = useState(false)
  const list = projects.data?.projects ?? []
  const { open, archived: shelved } = partitionProjects(list)

  return (
    <section className="card">
      <div className="card__head">
        <h2>Projects</h2>
        {!archived && !creating && (
          <button type="button" onClick={() => setCreating(true)}>
            New project
          </button>
        )}
      </div>

      {creating && (
        <NewProject
          groupId={groupId}
          projects={list}
          transactions={transactions}
          onDone={() => setCreating(false)}
        />
      )}

      {projects.isPending && <p className="muted">Fetching the projects…</p>}
      {projects.isError && <p className="bad">{projects.error.message}</p>}
      {projects.data && list.length === 0 && !creating && (
        <p className="muted note">
          A project gathers some of the group’s transactions — a weekend, a trip, the food —
          so they can be totted up and settled on their own.
        </p>
      )}

      {open.length > 0 && (
        <ul className="rows projects">
          {open.map((project) => (
            <ProjectRow key={project.id} project={project} groupId={groupId} navigate={navigate} />
          ))}
        </ul>
      )}

      {/*
        Archived ones are finished business: listed, because they are still
        read, and folded, because they are not what somebody opened the group
        to look at.
      */}
      {shelved.length > 0 && (
        <details className="projects__archived">
          <summary>
            Archived ({shelved.length})
          </summary>
          <ul className="rows projects">
            {shelved.map((project) => (
              <ProjectRow key={project.id} project={project} groupId={groupId} navigate={navigate} />
            ))}
          </ul>
        </details>
      )}
    </section>
  )
}
